import { getToken } from './auth';

// Configure API URL for different environments
const API_URL = process.env.NODE_ENV === 'development' 
  ? '' // Empty string for relative URLs in development (works with proxy)
  : process.env.REACT_APP_API_URL;

// Default API prefix path
const API_PREFIX = '/api/v1';

// Error types for better error handling
export enum ApiErrorType {
  UNAUTHORIZED = 'unauthorized',
  NOT_FOUND = 'not_found',
  SERVER_ERROR = 'server_error',
  NETWORK_ERROR = 'network_error',
  VALIDATION_ERROR = 'validation_error',
  UNKNOWN = 'unknown'
}

export class ApiError extends Error {
  type: ApiErrorType;
  statusCode?: number;
  
  constructor(message: string, type: ApiErrorType, statusCode?: number) {
    super(message);
    this.type = type;
    this.statusCode = statusCode;
    this.name = 'ApiError';
  }
}

// Base request function with authentication
export const apiRequest = async <T>(
  endpoint: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET',
  body?: any
): Promise<T> => {
  const token = await getToken();
  
  if (!token) {
    // Redirect to login page
    window.location.href = '/login';
    throw new ApiError('Authentication required', ApiErrorType.UNAUTHORIZED);
  }
  
  const headers: HeadersInit = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
  
  const config: RequestInit = {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  };
  
  try {
    // Ensure endpoint has the proper API prefix
    const fullEndpoint = endpoint.startsWith('/api/v1') 
      ? endpoint 
      : `${API_PREFIX}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
      
    const response = await fetch(`${API_URL}${fullEndpoint}`, config);
    
    // Handle different response statuses
    if (response.status === 401) {
      // Clear auth state on 401 Unauthorized
      console.error('Authentication token expired or invalid');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('id_token');
      sessionStorage.removeItem('user');
      
      // Redirect to login page with expired session message
      alert('Your session has expired. Please log in again.');
      window.location.href = '/login';
      throw new ApiError('Unauthorized access', ApiErrorType.UNAUTHORIZED, 401);
    }
    
    if (response.status === 404) {
      throw new ApiError('Resource not found', ApiErrorType.NOT_FOUND, 404);
    }
    
    if (response.status >= 400 && response.status < 500) {
      const data = await response.json().catch(() => ({}));
      throw new ApiError(
        data.message || 'Request validation failed',
        ApiErrorType.VALIDATION_ERROR,
        response.status
      );
    }
    
    if (response.status >= 500) {
      throw new ApiError('Server error', ApiErrorType.SERVER_ERROR, response.status);
    }
    
    if (!response.ok) {
      throw new ApiError('Unknown error', ApiErrorType.UNKNOWN, response.status);
    }
    
    // For successful responses
    if (response.status === 204) {
      return {} as T; // No content
    }
    
    return await response.json() as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    
    // Network errors
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new ApiError('Network error', ApiErrorType.NETWORK_ERROR);
    }
    
    throw new ApiError(
      error instanceof Error ? error.message : 'Unknown error',
      ApiErrorType.UNKNOWN
    );
  }
};

// Convenience methods for different HTTP verbs
export const get = <T>(endpoint: string): Promise<T> => {
  return apiRequest<T>(endpoint, 'GET');
};

export const post = <T>(endpoint: string, data: any): Promise<T> => {
  return apiRequest<T>(endpoint, 'POST', data);
};

export const put = <T>(endpoint: string, data: any): Promise<T> => {
  return apiRequest<T>(endpoint, 'PUT', data);
};

export const del = <T>(endpoint: string): Promise<T> => {
  return apiRequest<T>(endpoint, 'DELETE');
};

export const patch = <T>(endpoint: string, data: any): Promise<T> => {
  return apiRequest<T>(endpoint, 'PATCH', data);
};
